import React, { useState, useEffect } from 'react';
import {
  Typography, Box, Button, Paper, Grid, Card, CardContent,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Stack, useTheme, alpha, Chip, Divider, IconButton, Dialog,
  DialogTitle, DialogContent, DialogActions, Tooltip, Container, TextField, Alert,
  TablePagination
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Science as ScienceIcon,
  Assignment as AssignmentIcon,
  Settings as SettingsIcon,
  AttachMoney as AttachMoneyIcon,
  Biotech as BiotechIcon,
  Approval as ApprovalIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { supabase } from '../../../supabaseClient';
import { usePermissions } from '../../../utils/permissions';

const SummaryTab = ({ 
  formulation, 
  formulationIngredients = [], 
  phases = [],
  showSnackbar, 
  user, 
  onUpdate 
}) => {
  const theme = useTheme();
  const { canAccessPage, canEditPage, hasPermission, isManagerOrAbove, userRole, hierarchyLevel, canApproveFormulations } = usePermissions();
  const [loading, setLoading] = useState(false);
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [approving, setApproving] = useState(false);
  const [unapproveDialogOpen, setUnapproveDialogOpen] = useState(false);
  const [unapproving, setUnapproving] = useState(false);
  const [approvalSignature, setApprovalSignature] = useState('');
  const [signaturePreviewName, setSignaturePreviewName] = useState('');
  const [unapprovalSignature, setUnapprovalSignature] = useState('');
  const [unapprovalReason, setUnapprovalReason] = useState('');
  const [unapprovalSignaturePreview, setUnapprovalSignaturePreview] = useState('');
  const [approvalHistory, setApprovalHistory] = useState([]);
  const [tests, setTests] = useState([]);
  const [procedureSteps, setProcedureSteps] = useState([]);
  const [costs, setCosts] = useState({
    totalIngredientCost: 0,
    laborCost: 0,
    overheadCost: 0,
    totalCost: 0,
    costPerUnit: 0
  });
  const [rawMaterials, setRawMaterials] = useState([]);
  
  // Pagination state for approval history
  const [approvalPage, setApprovalPage] = useState(0);
  const [approvalRowsPerPage, setApprovalRowsPerPage] = useState(5);

  // Check permissions for summary tab specifically
  const canViewSummary = hasPermission('products.formulations.summary.view', 'read');
  const canEditSummary = hasPermission('products.formulations.summary.edit', 'write');
  const canDeleteSummary = hasPermission('products.formulations.summary.delete', 'delete');
  const canApprove = canApproveFormulations();

  const fetchTests = async () => {
    try {
      const { data, error } = await supabase
        .from('formulation_tests')
        .select('*')
        .eq('formulation_id', formulation.id);
        
      if (error) throw error;
      setTests(data || []);
    } catch (error) {
      console.error('Error fetching tests:', error);
    }
  };

  const fetchProcedureSteps = async () => {
    try {
      const { data, error } = await supabase
        .from('procedure_steps')
        .select('*')
        .eq('formulation_id', formulation.id)
        .order('step_order');
        
      if (error) throw error;
      setProcedureSteps(data || []);
    } catch (error) {
      console.error('Error fetching procedure steps:', error);
    }
  };

  const fetchRawMaterials = async () => {
    try {
      const { data, error } = await supabase
        .from('raw_materials')
        .select('*')
        .eq('company_id', formulation.company_id)
        .eq('is_active', true);
        
      if (error) throw error;
      setRawMaterials(data || []);
    } catch (error) {
      console.error('Error fetching raw materials:', error);
    }
  };

  const fetchApprovalHistory = async () => {
    try {
      // First get the approval history
      const { data: historyData, error: historyError } = await supabase
        .from('formulation_approval_history')
        .select('*')
        .eq('formulation_id', formulation.id)
        .order('performed_at', { ascending: false });
        
      if (historyError) throw historyError;
      
      if (!historyData || historyData.length === 0) {
        setApprovalHistory([]);
        return;
      }
      
      // Get unique user IDs
      const userIds = [...new Set(historyData.map(entry => entry.performed_by).filter(Boolean))];
      
      if (userIds.length === 0) {
        setApprovalHistory(historyData);
        return;
      }
      
      // Get profile data for each user using the security definer function
      const profilePromises = userIds.map(userId => 
        supabase.rpc('get_profile_by_user_id', { p_user_id: userId })
      );
      
      const profileResults = await Promise.all(profilePromises);
      const profileData = profileResults
        .filter(result => !result.error && result.data && result.data.length > 0)
        .map(result => result.data[0]);
      
      // Get user emails as fallback
      const { data: userEmailData } = await supabase
        .rpc('get_user_emails_for_company', { 
          p_company_id: formulation.company_id 
        });
      
      // Map the data together
      const enrichedData = historyData.map(entry => {
        const profile = profileData.find(p => p.id === entry.performed_by);
        const userEmail = userEmailData?.find(u => u.user_id === entry.performed_by);
        
        return {
          ...entry,
          profiles: profile || {
            id: entry.performed_by,
            first_name: null,
            last_name: null,
            email: userEmail?.email || null
          }
        };
      });
      
      setApprovalHistory(enrichedData);
    } catch (error) {
      console.error('Error fetching approval history:', error);
      setApprovalHistory([]);
    }
  };

  const calculateCosts = () => {
    if (!formulationIngredients.length || !rawMaterials.length) return;
    
    // Calculate ingredient costs
    let totalIngredientCost = 0;
    
    formulationIngredients.forEach(ingredient => {
      const rawMaterial = rawMaterials.find(rm => rm.id === ingredient.ingredient_id);
      if (rawMaterial && rawMaterial.unit_cost) {
        const amountNeeded = (ingredient.percentage * formulation.target_batch_size / 100);
        const cost = (amountNeeded / 1000) * rawMaterial.unit_cost; // Assuming unit_cost is per kg
        totalIngredientCost += cost;
      }
    });
    
    // Apply default percentages
    const laborCost = totalIngredientCost * 0.4; // 40% labor
    const overheadCost = (totalIngredientCost + laborCost) * 0.28; // 28% overhead
    const totalCost = totalIngredientCost + laborCost + overheadCost;
    
    const batchSize = formulation.target_batch_size || 100;
    const costPerUnit = totalCost / batchSize;
    
    setCosts({
      totalIngredientCost,
      laborCost,
      overheadCost,
      totalCost,
      costPerUnit
    });
  };

  const fetchFormulationCosts = async () => {
    try {
      // Get costs from formulation_costs table (saved by CostsTab)
      const { data: costsData, error: costsError } = await supabase
        .from('formulation_costs')
        .select('*')
        .eq('formulation_id', formulation.id)
        .order('calculated_at', { ascending: false })
        .limit(1)
        .single();
        
      if (costsError && costsError.code !== 'PGRST116') {
        throw costsError;
      }
      
      if (costsData) {
        setCosts({
          totalIngredientCost: costsData.raw_material_cost || 0,
          laborCost: costsData.labor_cost || 0,
          overheadCost: costsData.overhead_cost || 0,
          totalCost: costsData.total_cost || 0,
          costPerUnit: costsData.cost_per_unit || 0
        });
      } else {
        // Fallback to default calculation if no saved costs
        calculateCosts();
      }
    } catch (error) {
      console.error('Error fetching costs:', error);
      calculateCosts();
    }
  };

  useEffect(() => {
    if (formulation?.id) {
      fetchTests();
      fetchProcedureSteps();
      fetchRawMaterials();
      fetchFormulationCosts();
      fetchApprovalHistory();
    }
  }, [formulation?.id]);

  // Refetch costs when component mounts or formulation data changes
  useEffect(() => {
    if (formulation?.id) {
      fetchFormulationCosts();
    }
  }, [formulation?.costs_complete, formulation?.updated_at]);

  // Permission check
  if (!canViewSummary) {
    return (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        justifyContent: 'center',
        minHeight: '60vh',
        textAlign: 'center',
        px: 3
      }}>
        <Card sx={{
          p: 6,
          borderRadius: 4,
          border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
          background: 'white',
          boxShadow: `0 12px 40px ${alpha(theme.palette.grey[900], 0.08)}`,
          maxWidth: 500,
          width: '100%',
          position: 'relative',
          overflow: 'hidden',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: `0 16px 48px ${alpha(theme.palette.primary.main, 0.15)}`,
            border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
          },
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.03)}, transparent)`,
            zIndex: 0,
            opacity: 0.8,
            transition: 'opacity 0.3s ease'
          },
          '&:hover::before': {
            opacity: 1
          }
        }}>
          <Box sx={{ 
            p: 3, 
            borderRadius: 3, 
            background: `linear-gradient(${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.primary.main, 0.02)})`,
            border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
            mb: 6,
            display: 'inline-flex',
            boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.15)}`
          }}>
            <AssignmentIcon sx={{ fontSize: 48, color: 'text.secondary' }} />
          </Box>
          <Typography variant="h3" sx={{ 
            mb: 3, 
            fontWeight: 500, 
            color: 'text.primary',
            fontSize: '2.5rem',
            letterSpacing: '-0.8px'
          }}>
            Access Restricted
          </Typography>
          <Typography variant="h6" sx={{ 
            color: 'text.secondary', 
            mb: 3,
            fontWeight: 500,
            fontSize: '1.2rem'
          }}>
            Formulation Summary Required
          </Typography>
          <Typography variant="body1" sx={{ 
            color: 'text.secondary',
            lineHeight: 1.7,
            mb: 6,
            fontSize: '1.05rem',
            fontWeight: 500
          }}>
            You don't currently have the necessary permissions to access the Formulation Summary. Contact your system administrator to request access or verify your role permissions.
          </Typography>
        </Card>
      </Box>
    );
  }

  const handleApproveFormulation = () => {
    // Check if user has approve permission
    if (!canApprove) {
      showSnackbar('You do not have permission to approve formulations.', 'error');
      return;
    }
    
    // Check if all steps are complete
    const allStepsComplete = (
      formulation?.overview_complete &&
      formulation?.ingredients_complete &&
      formulation?.procedure_complete &&
      formulation?.tests_complete &&
      formulation?.costs_complete
    );
    
    if (!allStepsComplete) {
      showSnackbar('All steps must be completed before approval! Please ensure Overview, Ingredients, Procedure, Tests, and Costs are all marked complete.', 'error');
      return;
    }
    
    setApproveDialogOpen(true);
  };

  const handleConfirmApproval = async () => {
    // Double check permissions before proceeding
    if (!canApprove) {
      showSnackbar('You do not have permission to approve formulations.', 'error');
      setApproveDialogOpen(false);
      return;
    }
    
    if (!approvalSignature.trim()) {
      showSnackbar('Please provide your signature to approve the formulation', 'error');
      return;
    }

    setApproving(true);
    try {
      // Update formulation status
      const { error: updateError } = await supabase
        .from('formulations')
        .update({ 
          status: 'approved',
          approved_at: new Date().toISOString(),
          approved_by: user?.id,
          approval_signature: approvalSignature,
          unapproved_at: null,
          unapproved_by: null,
          unapproval_reason: null,
          unapproval_signature: null
        })
        .eq('id', formulation.id);
        
      if (updateError) throw updateError;

      // Add to approval history
      const { error: historyError } = await supabase
        .from('formulation_approval_history')
        .insert({
          formulation_id: formulation.id,
          action_type: 'approved',
          signature: approvalSignature,
          reason: null,
          performed_by: user?.id
        });

      if (historyError) throw historyError;
      
      showSnackbar('Formulation approved successfully!', 'success');
      setApproveDialogOpen(false);
      setApprovalSignature('');
      setSignaturePreviewName('');
      
      // Refresh data
      fetchApprovalHistory();
      if (onUpdate) {
        onUpdate();
      }
    } catch (error) {
      console.error('Error approving formulation:', error);
      showSnackbar('Error approving formulation: ' + error.message, 'error');
    } finally {
      setApproving(false);
    }
  };

  const handleUnapproveFormulation = () => {
    // Check if user has approve permission
    if (!canApprove) {
      showSnackbar('You do not have permission to unapprove formulations.', 'error');
      return;
    }
    
    setUnapproveDialogOpen(true);
  };

  const handleConfirmUnapproval = async () => {
    // Double check permissions before proceeding
    if (!canApprove) {
      showSnackbar('You do not have permission to unapprove formulations.', 'error');
      setUnapproveDialogOpen(false);
      return;
    }
    
    if (!unapprovalSignature.trim()) {
      showSnackbar('Please provide your signature to unapprove the formulation', 'error');
      return;
    }

    if (!unapprovalReason.trim()) {
      showSnackbar('Please provide a reason for unapproving the formulation', 'error');
      return;
    }

    setUnapproving(true);
    try {
      // Update formulation status
      const { error: updateError } = await supabase
        .from('formulations')
        .update({ 
          status: 'draft',
          unapproved_at: new Date().toISOString(),
          unapproved_by: user?.id,
          unapproval_reason: unapprovalReason,
          unapproval_signature: unapprovalSignature
        })
        .eq('id', formulation.id);
        
      if (updateError) throw updateError;

      // Add to approval history
      const { error: historyError } = await supabase
        .from('formulation_approval_history')
        .insert({
          formulation_id: formulation.id,
          action_type: 'unapproved',
          signature: unapprovalSignature,
          reason: unapprovalReason,
          performed_by: user?.id
        });

      if (historyError) throw historyError;
      
      showSnackbar('Formulation unapproved successfully!', 'success');
      setUnapproveDialogOpen(false);
      setUnapprovalSignature('');
      setUnapprovalReason('');
      setUnapprovalSignaturePreview('');
      
      // Refresh data
      fetchApprovalHistory();
      if (onUpdate) {
        onUpdate();
      }
    } catch (error) {
      console.error('Error unapproving formulation:', error);
      showSnackbar('Error unapproving formulation: ' + error.message, 'error');
    } finally {
      setUnapproving(false);
    }
  };

  // Calculate totals and completion
  const totalPercentage = formulationIngredients.reduce((sum, item) => sum + parseFloat(item.percentage || 0), 0);
  const completionChecks = {
    basicInfo: !!(formulation?.name && formulation?.target_batch_size && formulation?.description),
    ingredients: formulationIngredients.length > 0 && Math.abs(totalPercentage - 100) < 0.01,
    procedure: procedureSteps.length > 0,
    tests: tests.length > 0,
    specifications: !!(formulation?.ph_target || formulation?.viscosity_target || formulation?.appearance)
  };

  const overallCompletion = Object.values(completionChecks).filter(Boolean).length;
  const totalChecks = Object.keys(completionChecks).length;
  const completionPercentage = (overallCompletion / totalChecks) * 100;

  // Check if all steps are complete for approval
  const allStepsComplete = (
    formulation?.overview_complete &&
    formulation?.ingredients_complete &&
    formulation?.procedure_complete &&
    formulation?.tests_complete &&
    formulation?.costs_complete
  );
  
  const canApproveFormulation = formulation?.status !== 'approved' && allStepsComplete && canApprove;

  // Pagination handlers for approval history
  const handleApprovalPageChange = (event, newPage) => {
    setApprovalPage(newPage);
  };

  const handleApprovalRowsPerPageChange = (event) => {
    setApprovalRowsPerPage(parseInt(event.target.value, 10));
    setApprovalPage(0);
  };

  return (
    <Box
      sx={{
        pb: 4,
        display: 'flex',
        justifyContent: 'center',
        width: '100%'
      }}
    >
      <Box sx={{ maxWidth: '100vw', width: '100%', px: { xs: 2, sm: 3 } }}>
      {/* Header Section */}
      <Card 
        sx={{ 
          borderRadius: 3,
          mb: 2,
          border: `1px solid ${theme.palette.grey[400]}`,
          background: 'white',
          backdropFilter: 'blur(20px)',
          boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
          '&:hover': {
            transform: 'translateY(-1px)',
            boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`,
          },
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
        }}
      >
        <CardContent sx={{ p: 2.5 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <Box sx={{ flex: 1 }}>
              <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
                <Box sx={{ 
                  p: 1, 
                  borderRadius: 2, 
                  background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.primary.main, 0.05)})`,
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                  backdropFilter: 'blur(10px)'
                }}>
                  <AssignmentIcon sx={{ fontSize: 20, color: theme.palette.primary.main }} />
                </Box>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 500, color: theme.palette.text.primary, mb: 0.5 }}>
                    Formulation Summary
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                    Review all components before final approval
                  </Typography>
                </Box>
              </Stack>
              
              {/* Completion Stats */}
              <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h5" sx={{ 
                    fontWeight: 500, 
                    color: completionPercentage >= 80 ? 'success.main' : completionPercentage >= 60 ? 'warning.main' : 'error.main'
                  }}>
                    {Math.round(completionPercentage)}%
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                    Complete
                  </Typography>
                </Box>
                <Divider orientation="vertical" flexItem />
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h5" sx={{ fontWeight: 500, color: theme.palette.primary.main }}>
                    {formulationIngredients.length}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                    Ingredients
                  </Typography>
                </Box>
                <Divider orientation="vertical" flexItem />
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h5" sx={{ fontWeight: 500, color: theme.palette.primary.main }}>
                    {phases.length}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                    Phases
                  </Typography>
                </Box>
                <Divider orientation="vertical" flexItem />
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h5" sx={{ fontWeight: 500, color: theme.palette.primary.main }}>
                    {tests.length}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                    Tests
                  </Typography>
                </Box>
              </Stack>
            </Box>
            
            {canApprove ? (
              <>
                {canApproveFormulation ? (
                  <Button 
                    variant="contained" 
                    startIcon={<ApprovalIcon />}
                    onClick={handleApproveFormulation}
                    disabled={approving}
                    size="large"
                    sx={{ 
                      borderRadius: 3,
                      px: 4,
                      py: 2,
                      background: `linear-gradient(135deg, ${theme.palette.success.main}, ${theme.palette.success.dark})`,
                      color: 'white',
                      fontWeight: 500,
                      fontSize: '1rem',
                      boxShadow: `0 4px 12px ${alpha(theme.palette.success.main, 0.3)}`,
                      '&:hover': {
                        background: `linear-gradient(135deg, ${theme.palette.success.dark}, ${theme.palette.success.main})`,
                        transform: 'translateY(-2px)',
                        boxShadow: `0 8px 24px ${alpha(theme.palette.success.main, 0.4)}`
                      },
                      '&:disabled': {
                        background: alpha(theme.palette.grey[400], 0.5),
                        color: 'text.disabled'
                      },
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                    }}
                  >
                    {approving ? 'Approving...' : 'Approve Formulation'}
                  </Button>
                ) : formulation?.status !== 'approved' && (
                  <Tooltip 
                    title={
                      <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 500, mb: 1 }}>
                          Complete all steps to approve:
                        </Typography>
                        <Stack spacing={0.5}>
                          {!formulation?.overview_complete && (
                            <Typography variant="body2">• Overview not complete</Typography>
                          )}
                          {!formulation?.ingredients_complete && (
                            <Typography variant="body2">• Ingredients not complete</Typography>
                          )}
                          {!formulation?.procedure_complete && (
                            <Typography variant="body2">• Procedure not complete</Typography>
                          )}
                          {!formulation?.tests_complete && (
                            <Typography variant="body2">• Tests not complete</Typography>
                          )}
                          {!formulation?.costs_complete && (
                            <Typography variant="body2">• Costs not complete</Typography>
                          )}
                        </Stack>
                      </Box>
                    }
                    placement="bottom"
                    arrow
                  >
                    <Box>
                      <Button 
                        variant="outlined" 
                        startIcon={<WarningIcon />}
                        disabled
                        size="large"
                        sx={{ 
                          borderRadius: 3,
                          px: 4,
                          py: 2,
                          borderColor: 'warning.main',
                          color: 'warning.main',
                          fontWeight: 500,
                          fontSize: '1rem',
                          '&:disabled': {
                            borderColor: 'warning.main',
                            color: 'warning.main',
                            opacity: 0.7
                          }
                        }}
                      >
                        Steps Incomplete
                      </Button>
                    </Box>
                  </Tooltip>
                )}
              </>
            ) : formulation?.status !== 'approved' && (
              <Tooltip 
                title={
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 500, mb: 1 }}>
                      Approval Required
                    </Typography>
                    <Typography variant="body2">
                      Contact your administrator or manager to approve this formulation for review.
                    </Typography>
                  </Box>
                }
                placement="bottom"
                arrow
              >
                <Box>
                  <Button 
                    variant="outlined" 
                    startIcon={<InfoIcon />}
                    disabled
                    size="large"
                    sx={{ 
                      borderRadius: 3,
                      px: 4,
                      py: 2,
                      borderColor: 'info.main',
                      color: 'info.main',
                      fontWeight: 500,
                      fontSize: '1rem',
                      '&:disabled': {
                        borderColor: 'info.main',
                        color: 'info.main',
                        opacity: 0.7
                      }
                    }}
                  >
                    Contact Administrator
                  </Button>
                </Box>
              </Tooltip>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Approval Status Display */}
      {formulation?.status === 'approved' && formulation?.approval_signature && (
        <Card 
          sx={{ 
            borderRadius: 3,
            mb: 2,
            border: `1px solid ${theme.palette.success.main}`,
            background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.1)}, ${alpha(theme.palette.success.main, 0.05)})`,
            backdropFilter: 'blur(20px)',
            boxShadow: `0 8px 32px ${alpha(theme.palette.success.main, 0.12)}`,
            '&:hover': {
              transform: 'translateY(-1px)',
              boxShadow: `0 12px 48px ${alpha(theme.palette.success.main, 0.18)}`,
            },
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
          }}
        >
          <CardContent sx={{ p: 2.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ 
                p: 1, 
                borderRadius: 2, 
                background: alpha(theme.palette.success.main, 0.2),
                border: `1px solid ${alpha(theme.palette.success.main, 0.3)}`
              }}>
                <CheckCircleIcon sx={{ fontSize: 20, color: theme.palette.success.main }} />
              </Box>
              
              <Box sx={{ flex: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 500, color: theme.palette.success.main, mb: 0.5 }}>
                  Formulation Approved Successfully
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                  This formulation has been approved and is ready for production use
                </Typography>
                {canApprove && (
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleUnapproveFormulation}
                    sx={{
                      mt: 1,
                      borderColor: theme.palette.error.main,
                      color: theme.palette.error.main,
                      fontWeight: 500,
                      '&:hover': {
                        borderColor: theme.palette.error.dark,
                        backgroundColor: alpha(theme.palette.error.main, 0.04)
                      }
                    }}
                  >
                    Unapprove
                  </Button>
                )}
              </Box>

              <Box sx={{ 
                border: `1px solid ${theme.palette.success.main}`, 
                p: 2, 
                borderRadius: 2, 
                bgcolor: alpha(theme.palette.success.main, 0.1),
                minWidth: 200
              }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1, fontWeight: 500 }}>
                  APPROVED BY:
                </Typography>
                <Box sx={{ 
                  fontFamily: "'Brush Script MT', 'Brush Script Std', cursive", 
                  fontSize: '20px', 
                  color: theme.palette.success.main,
                  textShadow: '1px 1px 1px rgba(0,0,0,0.1)',
                  mb: 1
                }}>
                  {formulation.approval_signature}
                </Box>
                {formulation.approved_at && (
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                    Approved: {new Date(formulation.approved_at).toLocaleString('en-US', { 
                      year: 'numeric', 
                      month: '2-digit', 
                      day: '2-digit', 
                      hour: '2-digit', 
                      minute: '2-digit', 
                      hour12: false 
                    })}
                  </Typography>
                )}
              </Box>
            </Box>
          </CardContent>
        </Card>
      )}

      <Grid container spacing={2}>
        {/* Overview Summary */}
        <Grid item xs={12} md={6}>
          <Card sx={{ 
            height: '100%',
            borderRadius: 3,
            border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
            boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.08)}`,
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              transform: 'translateY(-1px)',
              boxShadow: '0 8px 25px rgba(0, 0, 0, 0.15)',
              border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
            }
          }}>
            <Box sx={{ 
              p: 2, 
              borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`,
              background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.primary.main, 0.02)})`
            }}>
              <Stack direction="row" spacing={2} alignItems="center">
                <Box sx={{ 
                  p: 1, 
                  borderRadius: 2, 
                  background: alpha(theme.palette.primary.main, 0.1),
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
                }}>
                  <AssignmentIcon fontSize="small" sx={{ color: theme.palette.primary.main }} />
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 500 }}>
                  General Information
                </Typography>
              </Stack>
            </Box>
            <CardContent sx={{ p: 2.5 }}>
              <Stack spacing={2}>
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                    NAME
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {formulation?.name || '—'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                    STATUS
                  </Typography>
                  <Box sx={{ mt: 1 }}>
                    <Chip 
                      label={formulation?.status?.charAt(0).toUpperCase() + formulation?.status?.slice(1)}
                      color={
                        formulation?.status === 'approved' ? 'success' :
                        formulation?.status === 'in_review' ? 'warning' :
                        formulation?.status === 'rejected' ? 'error' : 'default'
                      }
                      size="small"
                      sx={{ fontWeight: 500 }}
                    />
                  </Box>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                    BATCH SIZE
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {formulation?.target_batch_size} {formulation?.unit_of_measure}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                    VERSION
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {formulation?.version || '1.0'}
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Ingredients Summary */}
        <Grid item xs={12} md={6}>
          <Card sx={{ 
            height: '100%',
            borderRadius: 3,
            border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
            boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.08)}`,
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              transform: 'translateY(-1px)',
              boxShadow: '0 8px 25px rgba(0, 0, 0, 0.15)',
              border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
            }
          }}>
            <Box sx={{ 
              p: 2, 
              borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`,
              background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.primary.main, 0.02)})`
            }}>
              <Stack direction="row" spacing={2} alignItems="center">
                <Box sx={{ 
                  p: 1, 
                  borderRadius: 2, 
                  background: alpha(theme.palette.primary.main, 0.1),
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
                }}>
                  <ScienceIcon fontSize="small" sx={{ color: theme.palette.primary.main }} />
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 500 }}>
                  Ingredients Overview
                </Typography>
              </Stack>
            </Box>
            <CardContent sx={{ p: 2.5 }}>
              <Stack spacing={2}>
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                    TOTAL PERCENTAGE
                  </Typography>
                  <Typography 
                    variant="h5" 
                    sx={{ 
                      fontWeight: 500,
                      color: Math.abs(totalPercentage - 100) < 0.01 ? 'success.main' : 'warning.main'
                    }}
                  >
                    {totalPercentage.toFixed(2)}%
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                    INGREDIENT COUNT
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 500, color: theme.palette.primary.main }}>
                    {formulationIngredients.length}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                    PHASES
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 500, color: theme.palette.primary.main }}>
                    {phases.length}
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

                 {/* Cost Overview */}
         <Grid item xs={12} md={6}>
           <Card sx={{ 
             height: '100%',
             borderRadius: 3,
             border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
             boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.08)}`,
             transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
             '&:hover': {
               transform: 'translateY(-2px)',
               boxShadow: '0 8px 25px rgba(0, 0, 0, 0.15)',
               border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
             }
           }}>
            <Box sx={{ 
              p: 2, 
              borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`,
              background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.primary.main, 0.02)})`
            }}>
              <Stack direction="row" spacing={2} alignItems="center">
                <Box sx={{ 
                  p: 1, 
                  borderRadius: 2, 
                  background: alpha(theme.palette.primary.main, 0.1),
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
                }}>
                  <AttachMoneyIcon fontSize="small" sx={{ color: theme.palette.primary.main }} />
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 500 }}>
                  Cost Analysis
                </Typography>
              </Stack>
            </Box>
            <CardContent sx={{ p: 2.5 }}>
              <Stack spacing={2}>
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                    INGREDIENT COST
                  </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 500, color: theme.palette.primary.main }}>
                      ${costs.totalIngredientCost.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                    LABOR COST
                  </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 500, color: theme.palette.primary.main }}>
                      ${costs.laborCost.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                    OVERHEAD COST
                  </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 500, color: theme.palette.primary.main }}>
                      ${costs.overheadCost.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </Typography>
                </Box>
                <Divider />
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                    TOTAL BATCH COST
                  </Typography>
                    <Typography variant="h5" sx={{ fontWeight: 500, color: theme.palette.success.main }}>
                      ${costs.totalCost.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                    COST PER {formulation?.unit_of_measure?.toUpperCase()}
                  </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      ${costs.costPerUnit.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

                 {/* Technical Specifications */}
         <Grid item xs={12} md={6}>
           <Card sx={{ 
             height: '100%',
             borderRadius: 3,
             border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
             boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.08)}`,
             transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
             '&:hover': {
               transform: 'translateY(-2px)',
               boxShadow: '0 8px 25px rgba(0, 0, 0, 0.15)',
               border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
             }
           }}>
            <Box sx={{ 
              p: 2, 
              borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`,
              background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.primary.main, 0.02)})`
            }}>
              <Stack direction="row" spacing={2} alignItems="center">
                <Box sx={{ 
                  p: 1, 
                  borderRadius: 2, 
                  background: alpha(theme.palette.primary.main, 0.1),
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
                }}>
                  <SettingsIcon fontSize="small" sx={{ color: theme.palette.primary.main }} />
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 500 }}>
                  Technical Specs
                </Typography>
              </Stack>
            </Box>
            <CardContent sx={{ p: 2.5 }}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                    pH TARGET
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {formulation?.ph_target || '—'}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                    VISCOSITY
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {formulation?.viscosity_target || '—'}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                    APPEARANCE
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {formulation?.appearance || '—'}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                    YIELD
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {formulation?.yield_percentage ? `${formulation.yield_percentage}%` : '—'}
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

                 {/* Testing Summary */}
         <Grid item xs={12}>
           <Card sx={{ 
             borderRadius: 3,
             border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
             boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.08)}`,
             transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
             '&:hover': {
               transform: 'translateY(-2px)',
               boxShadow: '0 8px 25px rgba(0, 0, 0, 0.15)',
               border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
             }
           }}>
            <Box sx={{ 
              p: 2, 
              borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`,
              background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.primary.main, 0.02)})`
            }}>
              <Stack direction="row" spacing={2} alignItems="center">
                <Box sx={{ 
                  p: 1, 
                  borderRadius: 2, 
                  background: alpha(theme.palette.primary.main, 0.1),
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
                }}>
                  <BiotechIcon fontSize="small" sx={{ color: theme.palette.primary.main }} />
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 500 }}>
                  Testing & Quality Summary
                </Typography>
              </Stack>
            </Box>
            <CardContent sx={{ p: 2.5 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={3}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h5" sx={{ fontWeight: 500, color: theme.palette.primary.main }}>
                      {tests.length}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                      TOTAL TESTS
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h5" sx={{ 
                      fontWeight: 500, 
                      color: tests.filter(t => t.status === 'passed' || t.is_passed === true).length > 0 ? 'success.main' : 'text.secondary'
                    }}>
                      {tests.filter(t => t.status === 'passed' || t.is_passed === true).length}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                      PASSED
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h5" sx={{ 
                      fontWeight: 500, 
                      color: tests.filter(t => t.status === 'failed' || t.is_passed === false).length > 0 ? 'error.main' : 'text.secondary'
                    }}>
                      {tests.filter(t => t.status === 'failed' || t.is_passed === false).length}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                      FAILED
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h5" sx={{ fontWeight: 500, color: theme.palette.primary.main }}>
                      {procedureSteps.length}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                      PROCEDURE STEPS
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Approval History */}
        <Grid item xs={12}>
          <Card sx={{ 
            borderRadius: 3,
            border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
            boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.08)}`,
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              transform: 'translateY(-1px)',
              boxShadow: '0 8px 25px rgba(0, 0, 0, 0.15)',
              border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
            }
          }}>
            <Box sx={{ 
              p: 2, 
              borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`,
              background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.primary.main, 0.02)})`
            }}>
              <Stack direction="row" spacing={2} alignItems="center">
                <Box sx={{ 
                  p: 1, 
                  borderRadius: 2, 
                  background: alpha(theme.palette.primary.main, 0.1),
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
                }}>
                  <AssignmentIcon fontSize="small" sx={{ color: theme.palette.primary.main }} />
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 500 }}>
                  Approval History
                </Typography>
              </Stack>
            </Box>
            <CardContent sx={{ p: 2.5 }}>
              {approvalHistory.length > 0 ? (
                <Stack spacing={2}>
                  {(approvalRowsPerPage > 0
                    ? approvalHistory.slice(approvalPage * approvalRowsPerPage, approvalPage * approvalRowsPerPage + approvalRowsPerPage)
                    : approvalHistory
                  ).map((entry, index) => (
                    <Paper key={entry.id} sx={{ 
                      p: 2.5, 
                      background: entry.action_type === 'approved' 
                        ? `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.05)}, ${alpha(theme.palette.success.main, 0.02)})`
                        : `linear-gradient(135deg, ${alpha(theme.palette.error.main, 0.05)}, ${alpha(theme.palette.error.main, 0.02)})`,
                      borderRadius: 2,
                      border: `1px solid ${entry.action_type === 'approved' ? alpha(theme.palette.success.main, 0.2) : alpha(theme.palette.error.main, 0.2)}`,
                      boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.08)}`
                    }}>
                      <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} sm={3}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Chip 
                              label={entry.action_type === 'approved' ? 'APPROVED' : 'UNAPPROVED'}
                              size="small"
                              sx={{
                                fontWeight: 500,
                                borderRadius: 2,
                                fontSize: '0.75rem',
                                backgroundColor: entry.action_type === 'approved'
                                  ? alpha(theme.palette.success.main, 0.1)
                                  : alpha(theme.palette.error.main, 0.1),
                                color: entry.action_type === 'approved' ? theme.palette.success.main : theme.palette.error.main,
                                border: `1px solid ${entry.action_type === 'approved' ? theme.palette.success.main : theme.palette.error.main}`,
                              }}
                            />
                          </Box>
                        </Grid>
                        <Grid item xs={12} sm={3}>
                          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                            Signature
                          </Typography>
                          <Box sx={{ 
                            fontFamily: "'Brush Script MT', 'Brush Script Std', cursive", 
                            fontSize: '18px', 
                            color: entry.action_type === 'approved' ? theme.palette.success.main : theme.palette.error.main,
                            textShadow: '1px 1px 1px rgba(0,0,0,0.1)'
                          }}>
                            {entry.signature}
                          </Box>
                        </Grid>
                        <Grid item xs={12} sm={3}>
                          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                            Performed By
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {entry.profiles?.full_name 
                              ? entry.profiles.full_name
                              : entry.profiles?.first_name && entry.profiles?.last_name 
                              ? `${entry.profiles.first_name} ${entry.profiles.last_name}`
                              : entry.profiles?.first_name
                              ? entry.profiles.first_name
                              : entry.profiles?.last_name
                              ? entry.profiles.last_name
                              : entry.profiles?.email
                              ? entry.profiles.email.split('@')[0]
                              : entry.performed_by 
                              ? `User (${entry.performed_by.slice(0, 8)}...)`
                              : 'Unknown User'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {entry.profiles?.email || 'No email available'}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={3}>
                          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                            Date & Time
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {new Date(entry.performed_at).toLocaleString('en-US', { 
                              year: 'numeric', 
                              month: '2-digit', 
                              day: '2-digit', 
                              hour: '2-digit', 
                              minute: '2-digit', 
                              hour12: false 
                            })}
                          </Typography>
                        </Grid>
                        {entry.reason && (
                          <Grid item xs={12}>
                            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500, mb: 0.5 }}>
                              Reason
                            </Typography>
                            <Typography variant="body2" sx={{ 
                              fontWeight: 500,
                              p: 2,
                              backgroundColor: alpha(theme.palette.grey[100], 0.5),
                              borderRadius: 1,
                              border: `1px solid ${alpha(theme.palette.grey[300], 0.5)}`
                            }}>
                              {entry.reason}
                            </Typography>
                          </Grid>
                        )}
                      </Grid>
                    </Paper>
                  ))}
                </Stack>
              ) : (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <InfoIcon sx={{ fontSize: 48, color: theme.palette.grey[400], mb: 2 }} />
                  <Typography variant="h6" sx={{ fontWeight: 500, color: theme.palette.grey[600], mb: 1 }}>
                    No Approval History
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    This formulation has not been approved or unapproved yet
                  </Typography>
                </Box>
              )}
              
              {/* Pagination for Approval History */}
              {approvalHistory.length > 0 && (
                <Box sx={{ 
                  borderTop: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                  background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.primary.main, 0.02)})`,
                  borderRadius: '0 0 12px 12px',
                  mt: 2
                }}>
                  <TablePagination
                    rowsPerPageOptions={[5, 10, 25]}
                    component="div"
                    count={approvalHistory.length}
                    rowsPerPage={approvalRowsPerPage}
                    page={approvalPage}
                    onPageChange={handleApprovalPageChange}
                    onRowsPerPageChange={handleApprovalRowsPerPageChange}
                    sx={{ 
                      px: 3,
                      py: 2,
                      '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                        fontSize: '0.9rem',
                        fontWeight: 500,
                        color: theme.palette.text.primary
                      },
                      '& .MuiTablePagination-select': {
                        border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                        borderRadius: 2,
                        px: 2,
                        py: 1,
                        fontWeight: 500,
                        fontSize: '0.9rem',
                        background: 'white',
                        '&:hover': {
                          boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`
                        }
                      },
                      '& .MuiTablePagination-actions': {
                        '& .MuiIconButton-root': {
                          border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                          borderRadius: 2,
                          margin: '0 2px',
                          background: 'white',
                          color: theme.palette.primary.main,
                          fontWeight: 500,
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            background: theme.palette.primary.main,
                            color: 'white',
                            transform: 'translateY(-1px)',
                            boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.25)}`
                          },
                          '&.Mui-disabled': {
                            background: alpha(theme.palette.grey[300], 0.5),
                            color: theme.palette.grey[500],
                            border: `1px solid ${theme.palette.grey[300]}`,
                            transform: 'none',
                            boxShadow: 'none'
                          }
                        }
                      }
                    }}
                  />
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Approval Dialog */}
      <Dialog
        open={approveDialogOpen}
        onClose={() => setApproveDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            border: 'none',
            background: `linear-gradient(135deg, ${alpha(theme.palette.grey[50], 0.9)}, ${alpha(theme.palette.grey[100], 0.9)})`,
            backdropFilter: 'blur(20px)',
            boxShadow: `0 24px 48px ${alpha(theme.palette.success.main, 0.15)}`,
            overflow: 'hidden'
          }
        }}
      >
        <DialogTitle sx={{ 
          p: 2.5, 
          borderBottom: `1px solid ${alpha(theme.palette.success.main, 0.1)}`,
          background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.08)}, ${alpha(theme.palette.success.main, 0.04)})`,
          color: theme.palette.text.primary,
          fontWeight: 500,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <ApprovalIcon sx={{ mr: 2 }} />
            Approve Formulation
          </Box>
          <IconButton 
            onClick={() => setApproveDialogOpen(false)} 
            size="small"
            sx={{
              borderRadius: 2,
              border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
              '&:hover': {
                transform: 'scale(1.1)',
                border: `1px solid ${theme.palette.primary.main}`,
                boxShadow: `0 2px 8px ${alpha(theme.palette.grey[900], 0.2)}`
              }
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 2.5 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Alert severity="warning" sx={{ mb: 3, borderRadius: 2, mt: 3 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 500, mb: 1}}>
                  Are you sure you want to approve this formulation?
                </Typography>
                <Typography variant="body2">
                  This will mark the formulation as approved and ready for production. This action cannot be undone.
                </Typography>
              </Alert>
            </Grid>
            
            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 500, color: theme.palette.grey[700] }}>
                Enter your name to confirm approval:
              </Typography>
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                label="Approver Name"
                fullWidth
                autoFocus
                required
                value={approvalSignature}
                onChange={(e) => {
                  setApprovalSignature(e.target.value);
                  setSignaturePreviewName(e.target.value);
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': { borderColor: 'theme.palette.grey[400]', borderWidth: 1 },
                    '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                    '&.Mui-focused fieldset': { borderColor: theme.palette.grey[700], borderWidth: 1 },
                    backgroundColor: 'white'
                  }
                }}
              />
            </Grid>

            <Grid item xs={12}>
                              <Box sx={{ 
                  mt: 2, 
                  p: 2.5, 
                  border: `1px solid ${theme.palette.grey[400]}`, 
                  borderRadius: 2, 
                  backgroundColor: alpha(theme.palette.grey[50], 0.8)
                }}>
                <Typography variant="body2" gutterBottom sx={{ fontWeight: 500, color: theme.palette.grey[700] }}>
                  Preview: Your signature will appear in script font
                </Typography>
                <Box sx={{ 
                  fontFamily: "'Brush Script MT', 'Brush Script Std', cursive", 
                  fontSize: '28px', 
                  color: theme.palette.grey[800],
                  mt: 2,
                  mb: 1,
                  textShadow: '1px 1px 1px rgba(0,0,0,0.1)',
                  minHeight: '40px',
                  display: 'flex',
                  alignItems: 'center'
                }}>
                  {signaturePreviewName || 'Preview will appear here'}
                </Box>
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ 
          p: 2.5, 
          gap: 2, 
          borderTop: `1px solid ${alpha(theme.palette.success.main, 0.1)}`,
          background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.08)}, ${alpha(theme.palette.success.main, 0.04)})`
        }}>
          <Button 
            onClick={() => setApproveDialogOpen(false)}
            sx={{ 
              borderRadius: 2,
              px: 3,
              py: 1.5,
              color: theme.palette.text.secondary,
              fontWeight: 500,
              '&:hover': {
                backgroundColor: alpha(theme.palette.success.main, 0.04)
              }
            }}
          >
            Cancel
          </Button>
          {canApprove && (
            <Button 
              onClick={handleConfirmApproval}
              variant="contained"
              disabled={approving || !approvalSignature.trim()}
            sx={{
              borderRadius: 2,
              px: 4,
              py: 1.5,
              background: `linear-gradient(135deg, ${theme.palette.success.main}, ${theme.palette.success.dark})`,
              color: 'white',
              fontWeight: 500,
              boxShadow: `0 4px 12px ${alpha(theme.palette.success.main, 0.3)}`,
              '&:hover': {
                background: `linear-gradient(135deg, ${theme.palette.success.dark}, ${theme.palette.success.main})`,
                boxShadow: `0 6px 20px ${alpha(theme.palette.success.main, 0.4)}`
              },
              '&:disabled': {
                background: theme.palette.grey[400],
                color: theme.palette.grey[100]
              }
            }}
            >
              {approving ? 'Approving...' : 'Confirm Approval'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Unapprove Dialog */}
      <Dialog
        open={unapproveDialogOpen}
        onClose={() => setUnapproveDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            border: `1px solid ${theme.palette.grey[300]}`,
            background: `linear-gradient(135deg, ${alpha(theme.palette.grey[50], 0.9)}, ${alpha(theme.palette.grey[100], 0.9)})`,
            backdropFilter: 'blur(20px)',
            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.1)',
            overflow: 'hidden'
          }
        }}
      >
        <DialogTitle sx={{ 
          p: 2.5, 
          borderBottom: `1px solid ${alpha(theme.palette.error.main, 0.1)}`,
          background: `linear-gradient(135deg, ${alpha(theme.palette.error.main, 0.08)}, ${alpha(theme.palette.error.main, 0.04)})`,
          color: theme.palette.text.primary,
          fontWeight: 500,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <WarningIcon sx={{ mr: 2, color: theme.palette.error.main }} />
            Unapprove Formulation
          </Box>
          <IconButton 
            onClick={() => setUnapproveDialogOpen(false)} 
            size="small"
            sx={{
              borderRadius: 2,
              border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
              '&:hover': {
                transform: 'scale(1.1)',
                border: `1px solid ${theme.palette.primary.main}`,
                boxShadow: `0 2px 8px ${alpha(theme.palette.grey[900], 0.2)}`
              }
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 2.5 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Alert severity="error" sx={{ mb: 3, borderRadius: 2, mt: 3 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 500, mb: 1, color: '#000' }}>
                  Are you sure you want to unapprove this formulation?
                </Typography>
                <Typography variant="body2" sx={{ color: '#000' }}>
                  This will mark the formulation as draft and require re-approval. This action will be logged in the approval history.
                </Typography>
              </Alert>
            </Grid>
            
            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 500, color: theme.palette.grey[700] }}>
                Reason for unapproval (required):
              </Typography>
              <TextField
                label="Reason"
                fullWidth
                multiline
                rows={3}
                required
                value={unapprovalReason}
                onChange={(e) => setUnapprovalReason(e.target.value)}
                sx={{
                  mb: 2,
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                    '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                    '&.Mui-focused fieldset': { borderColor: theme.palette.grey[700], borderWidth: 1 },
                    backgroundColor: 'white'
                  }
                }}
              />
            </Grid>
            
            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 500, color: theme.palette.grey[700] }}>
                Enter your name to confirm unapproval:
              </Typography>
              <TextField
                label="Approver Name"
                fullWidth
                required
                value={unapprovalSignature}
                onChange={(e) => {
                  setUnapprovalSignature(e.target.value);
                  setUnapprovalSignaturePreview(e.target.value);
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                    '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                    '&.Mui-focused fieldset': { borderColor: theme.palette.grey[700], borderWidth: 1 },
                    backgroundColor: 'white'
                  }
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ 
                mt: 2, 
                p: 2.5, 
                border: `1px solid ${theme.palette.grey[400]}`, 
                borderRadius: 2, 
                backgroundColor: 'white'
              }}>
                <Typography variant="body2" gutterBottom sx={{ fontWeight: 500, color: theme.palette.grey[700] }}>
                  Preview: Your signature will appear in script font
                </Typography>
                <Box sx={{ 
                  fontFamily: "'Brush Script MT', 'Brush Script Std', cursive", 
                  fontSize: '28px', 
                  color: 'theme.palette.grey[800]',
                  mt: 2,
                  mb: 1,
                  textShadow: '1px 1px 1px rgba(0,0,0,0.1)',
                  minHeight: '40px',
                  display: 'flex',
                  alignItems: 'center'
                }}>
                  {unapprovalSignaturePreview || 'Preview will appear here'}
                </Box>
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ 
          p: 2.5, 
          gap: 2, 
          borderTop: `1px solid ${alpha(theme.palette.error.main, 0.1)}`,
          background: `linear-gradient(135deg, ${alpha(theme.palette.error.main, 0.08)}, ${alpha(theme.palette.error.main, 0.04)})`
        }}>
          <Button 
            onClick={() => setUnapproveDialogOpen(false)}
            sx={{
              borderRadius: 2,
              px: 3,
              py: 1,
              color: theme.palette.grey[600],
              '&:hover': {
                backgroundColor: alpha(theme.palette.grey[200], 0.5)
              }
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleConfirmUnapproval} 
            variant="contained"
            disabled={unapproving || !unapprovalSignature.trim() || !unapprovalReason.trim()}
            sx={{
              borderRadius: 2,
              px: 4,
              py: 1,
              background: `linear-gradient(135deg, ${theme.palette.error.main}, ${theme.palette.error.dark})`,
              color: 'white',
              fontWeight: 500,
              boxShadow: `0 4px 12px ${alpha(theme.palette.error.main, 0.3)}`,
              '&:hover': {
                background: `linear-gradient(135deg, ${theme.palette.error.dark}, ${theme.palette.error.main})`,
                transform: 'translateY(-1px)',
                boxShadow: `0 6px 20px ${alpha(theme.palette.error.main, 0.4)}`
              },
              '&:disabled': {
                background: alpha(theme.palette.grey[400], 0.5),
                color: 'text.disabled'
              },
              transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
            }}
          >
            {unapproving ? 'Unapproving...' : 'Confirm Unapproval'}
          </Button>
        </DialogActions>
      </Dialog>
      </Box>
    </Box>
  );
};

export default SummaryTab;